import { Stack } from "expo-router";
import { useEffect, useState } from "react";
import { View } from "react-native";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";
import { ActionSheetProvider } from "@expo/react-native-action-sheet";
import { useUnistyles } from "react-native-unistyles";
import { AppPersistQueryClientProvider } from "@src/features/query-client";
import { FontsProvider, resolveFontFamily } from "@src/providers/fonts-provider";
import { FontFamilyProvider } from "@visioni/a3s-ui/components";
import { IconProvider, iconRegistry } from "@visioni/a3s-ui/icons";
import { AnalyticsProvider } from "@src/providers/analytics-provider";
import { AuthProvider, useAuthStore } from "@src/features/auth";
import { UserProvider, TenantProvider, AppNotConfiguredView } from "@src/features/tenants";
import { networkReachability } from "@src/utils/network";
import { initAmplitude } from "@src/services/analytics/amplitude";
import { initFeatureFlags, hydrateFeatureFlagsFromCache } from "@src/services/feature-flags/feature-flags";
import { sizes } from "@src/constants/sizes";
import "react-native-reanimated";
import { BackgroundGradient } from "@src/components/BackgroundGradient";
import { isE2ETest } from "@src/modules/LaunchArguments";
import * as SplashScreen from "expo-splash-screen";
import { ForceUpgradeModal } from "@src/components/ForceUpgradeModal";
import { useForceUpgrade } from "@src/hooks/useForceUpgrade";
import { Toaster } from "@src/components/Toaster";
import { DevToolsProvider } from "@src/providers/dev-tools-provider";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ImpersonationBanner } from "@src/features/auth/components/ImpersonationBanner";

SplashScreen.preventAutoHideAsync();

export default function RootLayout() {
  const [isReady, setIsReady] = useState(false);
  const [featureFlagsFailedToLoad, setFeatureFlagsFailedToLoad] = useState(false);
  const { showForceUpgrade, forceUpgradeConfig, checkForceUpgrade } = useForceUpgrade();

  const handleRetryFeatureFlags = async () => {
    const success = await initFeatureFlags();
    if (success) {
      setFeatureFlagsFailedToLoad(false);
    }
  };

  useEffect(() => {
    const prepareApp = async () => {
      try {
        if (isE2ETest()) {
          setIsReady(true);
          return;
        }

        networkReachability.initialize();

        await initAmplitude();

        const authStore = useAuthStore.getState();
        await authStore.initialize();

        hydrateFeatureFlagsFromCache();

        // Re-fetch flags now that auth context is available
        const featureFlagsAvailable = await initFeatureFlags();
        if (!featureFlagsAvailable) {
          setFeatureFlagsFailedToLoad(true);
        }

        // Fire-and-forget force upgrade check
        void checkForceUpgrade();

        setIsReady(true);
      } catch (error) {
        console.warn("App init error:", error);
        setIsReady(true);
      } finally {
        SplashScreen.setOptions({
          fade: true,
          duration: 600,
        });
        void SplashScreen.hideAsync();
      }
    };

    prepareApp();

    return () => {
      networkReachability.cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- baseline 2026-02-19
  }, []);

  const { theme } = useUnistyles();
  const backgroundColor = theme.colors.background as string;

  if (featureFlagsFailedToLoad) {
    return <AppNotConfiguredView onRetry={() => void handleRetryFeatureFlags()} />;
  }

  const stackContent = (
    <Stack
      screenOptions={{
        headerStyle: {
          backgroundColor: theme.colors.background,
        },
        headerTintColor: theme.colors.text,
        headerBackButtonDisplayMode: "minimal",
        contentStyle: {
          backgroundColor: theme.colors.background,
        },
      }}
    >
      <Stack.Screen name="index" options={{ headerShown: false }} />
      <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
      <Stack.Screen name="development/theme-demo" options={{ title: "Theme Demo" }} />
      <Stack.Screen name="development/components/toast" options={{ title: "Toast" }} />
      <Stack.Screen name="development/feature-flags" options={{ title: "Feature Flags" }} />
      <Stack.Screen
        name="agent"
        options={({ route }: { route: { params?: { title?: string } } }) => ({
          title: route.params?.title ?? "AI Schedule Assistant",
        })}
      />
      <Stack.Screen name="contacts/activity-notification" options={{ title: "Activity Notification" }} />
      <Stack.Screen name="contacts/site-alarm" options={{ title: "Site Alarm" }} />
      <Stack.Screen name="contacts/manage-access" options={{ title: "Manage access" }} />
      <Stack.Screen name="contacts/add-users-to-camera" options={{ title: "Add to camera" }} />
      <Stack.Screen name="contacts/add-email-contact" options={{ title: "Add Email Contact" }} />
      <Stack.Screen name="contacts/add-sms-contact" options={{ title: "Add SMS Contact" }} />
      <Stack.Screen name="alarm-settings" options={{ title: "Alarm Settings" }} />
      <Stack.Screen name="auth/login" options={{ title: "Login", headerShown: false }} />
      <Stack.Screen name="auth/forgot-password" options={{ title: "Forgot Password", headerShown: false }} />
      <Stack.Screen name="auth/biometrics" options={{ title: "Biometrics", headerShown: false }} />
      <Stack.Screen name="auth/biometric-verify" options={{ title: "Verify", headerShown: false }} />
      <Stack.Screen name="auth/pin" options={{ title: "Pin", headerShown: false }} />
      <Stack.Screen name="auth/confirm-pin" options={{ title: "Confirm Pin", headerShown: false }} />
      <Stack.Screen name="auth/insert-pin" options={{ title: "Insert Pin", headerShown: false }} />
      <Stack.Screen name="auth/change-pin" options={{ title: "Change Pin", headerShown: false }} />
      <Stack.Screen name="tenants/[tenantId]" options={{ headerShown: false }} />
      <Stack.Screen name="not-found" options={{ title: "Not Found", headerShown: false }} />
    </Stack>
  );

  return (
    <BackgroundGradient>
      <SafeAreaProvider>
        <GestureHandlerRootView
          // @ts-expect-error baseline 2026-02-16
          style={{
            flex: 1,
            minHeight: "100vh",
            width: "100%",
            backgroundColor: theme.colors.background,
          }}
          testID="screen-root"
        >
          <FontsProvider>
            <FontFamilyProvider resolve={resolveFontFamily}>
              <IconProvider registry={iconRegistry}>
                <AppPersistQueryClientProvider>
                  <DevToolsProvider>
                    {__DEV__ && <ReactQueryDevtools initialIsOpen={false} />}
                    <UserProvider>
                      <TenantProvider>
                        <BottomSheetModalProvider>
                          <ActionSheetProvider>
                            <AuthProvider shouldShowReactNativeScreens={true}>
                              <AnalyticsProvider>
                                <View
                                  style={{
                                    flex: 1,
                                    maxWidth: sizes.maxWidthWeb,
                                    width: "100%",
                                    alignSelf: "center",
                                    backgroundColor: theme.colors.background,
                                  }}
                                >
                                  <ImpersonationBanner />
                                  {stackContent}
                                </View>
                              </AnalyticsProvider>
                            </AuthProvider>
                          </ActionSheetProvider>
                        </BottomSheetModalProvider>
                        <Toaster />
                      </TenantProvider>
                    </UserProvider>
                  </DevToolsProvider>
                </AppPersistQueryClientProvider>
                <ForceUpgradeModal
                  visible={showForceUpgrade}
                  upgradeMessage={forceUpgradeConfig.upgradeMessage}
                  appStoreUrl={forceUpgradeConfig.appStoreUrl}
                  playStoreUrl={forceUpgradeConfig.playStoreUrl}
                />
              </IconProvider>
            </FontFamilyProvider>
          </FontsProvider>
        </GestureHandlerRootView>
      </SafeAreaProvider>
    </BackgroundGradient>
  );
}
